import { ApolloClient } from '@apollo/client';
import { PostSubcategory } from '../../domain/postCategories';
import { Content, fetchPostsWithAds, fetchVideos } from './fetchPostsData';
import { resolved } from '../../utility/promiseHelper';
import { fifthInEverySix } from '../../ads/adPlacementLogic';

export const fetchNewsLandingPageData = async (
  client: ApolloClient<unknown>,
  excludePostIds: number[],
) => {
  const newsLandingPageRequirement = {
    propertyNews: 6,
    generalNews: 6,
    featuredVideos: 3,
  };

  const [propertyNewsData, generalNewsData, videoData] =
    await Promise.allSettled([
      fetchPostsWithAds(
        client,
        newsLandingPageRequirement.propertyNews,
        0,
        {
          postCategory: [PostSubcategory.PropertyNews],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchPostsWithAds(
        client,
        newsLandingPageRequirement.generalNews,
        0,
        {
          postCategory: [PostSubcategory.GeneralNews],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchVideos(client, newsLandingPageRequirement.featuredVideos, {
        postCategory: [
          PostSubcategory.PropertyNews,
          PostSubcategory.GeneralNews,
        ],
      }),
    ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };
  return {
    propertyNews: resolved(propertyNewsData, defaultContents).contents,
    generalNews: resolved(generalNewsData, defaultContents).contents,
    video: resolved(videoData, { featuredVideos: [], heroVideo: null }),
  };
};

export const fetchGuidesLandingPageData = async (
  client: ApolloClient<unknown>,
  excludePostIds: number[],
) => {
  const guidesLandingPageRequirement = {
    buyAndSell: 6,
    financial: 6,
    investment: 6,
  };

  const [buyAndSellData, financialData, investmentData] =
    await Promise.allSettled([
      fetchPostsWithAds(
        client,
        guidesLandingPageRequirement.buyAndSell,
        0,
        {
          postCategory: [PostSubcategory.BuyAndSell],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchPostsWithAds(
        client,
        guidesLandingPageRequirement.financial,
        0,
        {
          postCategory: [PostSubcategory.Finance],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchPostsWithAds(
        client,
        guidesLandingPageRequirement.investment,
        0,
        {
          postCategory: [PostSubcategory.Investment],
        },
        fifthInEverySix,
        excludePostIds,
      ),
    ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };
  return {
    buyAndSell: resolved(buyAndSellData, defaultContents).contents,
    financial: resolved(financialData, defaultContents).contents,
    investment: resolved(investmentData, defaultContents).contents,
  };
};

export const fetchDecorLandingPageData = async (
  client: ApolloClient<unknown>,
  excludePostIds: number[],
) => {
  const decorLandingPageRequirement = {
    interiorInspiration: 6,
    renovation: 6,
    maintenance: 6,
    featuredVideos: 3,
  };

  const [interiorInspirationData, renovationData, maintenanceData, videoData] =
    await Promise.allSettled([
      fetchPostsWithAds(
        client,
        decorLandingPageRequirement.interiorInspiration,
        0,
        {
          postCategory: [PostSubcategory.InteriorInspiration],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchPostsWithAds(
        client,
        decorLandingPageRequirement.renovation,
        0,
        {
          postCategory: [PostSubcategory.Renovation],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchPostsWithAds(
        client,
        decorLandingPageRequirement.maintenance,
        0,
        {
          postCategory: [PostSubcategory.Maintenance],
        },
        fifthInEverySix,
        excludePostIds,
      ),
      fetchVideos(client, decorLandingPageRequirement.featuredVideos, {
        postCategory: [
          PostSubcategory.InteriorInspiration,
          PostSubcategory.Renovation,
          PostSubcategory.Maintenance,
        ],
      }),
    ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };
  return {
    interiorInspiration: resolved(interiorInspirationData, defaultContents)
      .contents,
    renovation: resolved(renovationData, defaultContents).contents,
    maintenance: resolved(maintenanceData, defaultContents).contents,
    video: resolved(videoData, { featuredVideos: [], heroVideo: null }),
  };
};
